import React from "react";
import { useEffect, useRef, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { Paginator } from "primereact/paginator";
import FilterDropdown from "../../components/filterDropdown/FilterDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { InventoryHistoryViewModel } from "../../viewmodels/inventoryHistory/InventoryHistoryViewModel";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";

const InventoryHistory = (props) => {
  const { logout } = props;

  const lotNumberBody = (item) => (
    <CopyableIdentifier value={item.lot_number} description="lot number" />
  );

  const {
    getInventoryHistory,
    loading,
    inventoryHistoryData,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getAllPlus,
    printInventorySummaryReport,
    getWarehouseLocations,
    getClassTypes,
    getCategoryTypes,
    exportInventoryPageToExcel,
    exportInProgress,
    setEmptyModelOpen,
  } = InventoryHistoryViewModel(logout);

  var currentPage = 1;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getInventoryHistory(null, currentPage, filters);
    getAllPlus();
    getWarehouseLocations();
    getClassTypes();
    getCategoryTypes();
  }, []);

  const [first, setFirst] = useState(1);
  const [rows, setRows] = useState(20);

  const handleSearch = () => {
    getInventoryHistory(searchRef.current.value, currentPage, filters);
  };

  const handlePagination = (event) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getInventoryHistory(searchRef.current.value, currentPage, filters);
  };

  const searchRef = useRef();

  const bodyProductName = (item) => {
    return (
      <div>
        {item.product_name} <br />{" "}
        <span className="font-bold">PLU: {item.plu}</span>
      </div>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <p>
        {item.net_weight_kg}kg <br />
        {item.net_weight_lbs}lbs
      </p>
    );
  };

  const parentProductionDate = (item) => {
    return <p>{item.date}</p>;
  };

  const bodyParentPieceCount = (item) => {
    return <p>{item.piece_count} pieces</p>;
  };

  const bodyTotalCost = (item) => {
    return <p>${item.total_cost}</p>;
  };

  const averageTotalCost = (item) => {
    return <p>${item.avg_unit_cost}</p>;
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Inventory History
              </h1>
              <div className="mt-5 flex rounded-md">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="search"
                    name="searchInventory"
                    id="searchInventory"
                    className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search inventory"
                    ref={searchRef}
                    onKeyDown={(e) =>
                      e.key === "Enter" ? handleSearch() : null
                    }
                  />
                </div>
                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>

                <div>
                  <button
                    className={`ml-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                      exportInProgress || !filters.date
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                    onClick={() => {
                      if (!filters || !filters.date) {
                        setEmptyModelOpen({
                          childComponent: (
                            <ModelErrorComponent
                              setOpen={setEmptyModelOpen}
                              title={"Date required"}
                              subtitle={
                                "Please select a date in the filters before exporting."
                              }
                            />
                          ),
                          open: true,
                        });
                        return;
                      }
                      exportInventoryPageToExcel(
                        searchRef.current.value,
                        filters,
                        currentPage
                      );
                    }}
                    disabled={exportInProgress || !filters.date}
                    title={
                      !filters.date
                        ? "Select a date in the filters to enable export"
                        : "Export current page to Excel"
                    }
                  >
                    {exportInProgress ? "Exporting..." : "Export Excel"}
                  </button>
                </div>

                <FilterDropdown
                  actionCall={() =>
                    getInventoryHistory(
                      searchRef.current.value,
                      currentPage,
                      filters
                    )
                  }
                  filterComponents={filterComponents}
                  resetFilters={() => {
                    resetFilters();
                  }}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <div>
                    <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                      <div className="animate-pulse flex space-x-4">
                        Loading
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden card-custom">
                    <DataTable
                      value={inventoryHistoryData.data}
                      dataKey="plu"
                      tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm inventory-history-items-table"
                      cellClassName="p-2 px-5 border-1-bottom-gray text-sm"
                    >
                      <Column
                        header="Product Name"
                        body={bodyProductName}
                        style={{ width: "25%" }}
                      />
                      <Column
                        header="Lot Number"
                        body={lotNumberBody}
                        style={{ width: "20%" }}
                      />
                      <Column
                        header="Date"
                        field={parentProductionDate}
                        style={{ width: "15%" }}
                      />
                      <Column
                        header="Total Weight"
                        body={bodyParentTotalWeight}
                        style={{ width: "10%", textAlign: "right" }}
                      />
                      <Column
                        header="Total Pieces"
                        body={bodyParentPieceCount}
                        style={{ width: "10%", textAlign: "right" }}
                      />
                      <Column
                        header="Total Cost"
                        body={bodyTotalCost}
                        style={{ width: "10%", textAlign: "right" }}
                      />
                      <Column
                        header="Ave Cost"
                        body={averageTotalCost}
                        style={{ width: "10%", textAlign: "right" }}
                      />
                    </DataTable>
                  </div>
                )}
              </div>
              <div className="w-full">
                <Paginator
                  first={first}
                  rows={rows}
                  totalRecords={inventoryHistoryData.total}
                  onPageChange={handlePagination}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryHistory;
